export type CoachBox = {
  top: number;
  left: number;
  width: number;
  height: number;
  /** Bottom-pinned cards grow upward and stay above the instruction bar. */
  pin: "top" | "bottom";
};

/**
 * The quick tip is a small card, not a fullscreen blocker.
 * On a phone it sits along the bottom, above the instruction bar, so the
 * middle of the board stays clear.
 */
export function coachCardBox(
  viewport: { width: number; height: number },
  target: { top: number; bottom: number; left: number } | null,
  bottomInset = 8,
): CoachBox {
  const inset = Math.max(8, bottomInset);
  const narrow = viewport.width < 600;
  if (narrow) {
    const height = 160;
    const margin = 8;
    const width = Math.max(0, viewport.width - margin * 2);
    const bottomTop = Math.max(margin, viewport.height - height - inset);
    const targetHeight = target ? target.bottom - target.top : 0;
    const targetIsBoard = targetHeight > viewport.height * 0.45;
    const bottomOverlapsTarget =
      !!target &&
      !targetIsBoard &&
      bottomTop < target.bottom &&
      bottomTop + height > target.top;
    if (bottomOverlapsTarget && target) {
      const roomAbove = target.top - margin - 12;
      if (roomAbove >= 96) {
        const fitted = Math.min(height, roomAbove);
        return {
          top: target.top - fitted - 12,
          left: margin,
          width,
          height: fitted,
          pin: "top",
        };
      }
    }
    return {
      top: bottomTop,
      left: margin,
      width,
      height,
      pin: "bottom",
    };
  }

  const width = 320;
  const height = 168;
  const left = target
    ? Math.min(Math.max(12, target.left), Math.max(12, viewport.width - width - 12))
    : Math.max(12, viewport.width - width - 16);
  let top = target ? target.bottom + 12 : 72;
  const floor = viewport.height - inset - height;
  if (top > floor) {
    const above = (target?.top ?? 80) - height - 12;
    top = above >= 8 ? above : Math.max(8, floor);
  }
  return { top, left, width, height, pin: "top" };
}

export function boxContainsPoint(box: CoachBox, x: number, y: number): boolean {
  return (
    x >= box.left &&
    x <= box.left + box.width &&
    y >= box.top &&
    y <= box.top + box.height
  );
}
